import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/core/AuthContext';
import { useClientDetail } from '../lib/useClientDetail';
import { useTrainingPlan } from '../lib/useTrainingPlan';
import { useTrainerTemplates } from '../lib/useTrainerTemplates';
import { AssignmentList } from '../components/trainer/AssignmentList';
import { TrainerSessionsList } from '../components/trainer/TrainerSessionsList';
import { SessionReviewPanel } from '../components/trainer/SessionReviewPanel';
import { TrainingPlanSection } from '../components/trainer/TrainingPlanSection';
import { Button } from '../components/ui';
import { ALERTS } from '../styles/classNames';
import { formatPhoneForDisplay } from '@/shared/utils/phone';

export function TrainerClientDetail() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const trainerId = userData?.trainerProfileId || userData?.trainerId;
  const { client, assignments, sessions, saveNotes, error } = useClientDetail(
    trainerId,
    clientId
  );
  const { templates } = useTrainerTemplates(trainerId);
  const clientUserId = client?.status === 'linked' ? client.userId : undefined;
  const {
    plan,
    loading: planLoading,
    error: planError,
    addToRotation,
    removeFromRotation,
    reorderRotation,
    addToDailyRotation,
    removeFromDailyRotation,
    reorderDailyRotation,
  } = useTrainingPlan(clientUserId);
  const [notes, setNotes] = useState('');
  const [reviewSessionId, setReviewSessionId] = useState<string | null>(null);

  useEffect(() => {
    if (client?.trainerNotes !== undefined && client.trainerNotes !== notes) {
      setNotes(client.trainerNotes);
    }
  }, [client?.trainerNotes, notes]);

  if (!trainerId) {
    navigate('/trainer');
    return null;
  }

  return (
    <div className="min-h-screen p-6 text-slate-50">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-400">Client</p>
            <h1 className="text-3xl font-semibold">
              {client?.displayName || client?.phone || 'Client'}
            </h1>
            <p className="text-sm text-slate-400">
              {client?.phone ? formatPhoneForDisplay(client.phone) : 'No phone set'}
            </p>
          </div>
          <Link
            to="/trainer"
            className="glass-hover px-4 py-2 rounded-lg border border-white/10"
          >
            Back
          </Link>
        </div>

        {error && (
          <div className={ALERTS.error}>{error}</div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-2xl p-4 border border-white/10 space-y-3"
        >
          <h2 className="text-xl font-semibold">Link status</h2>
          <p className="text-sm font-medium text-slate-200">
            Status:{' '}
            {client?.status === 'linked'
              ? 'Linked'
              : client?.status === 'declined'
                ? 'Declined'
                : 'Pending'}
          </p>
          <p className="text-sm text-slate-300">
            {client?.status === 'linked'
              ? 'This client accepted your invite and is linked to your account.'
              : client?.status === 'declined'
                ? 'This client declined the invite.'
                : 'This invite is still pending. Client data stays locked until they accept.'}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-2xl p-4 border border-white/10 space-y-3"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Trainer notes</h2>
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                await saveNotes(notes);
              }}
            >
              Save notes
            </Button>
          </div>
          <textarea
            className="form-textarea max-w-none"
            placeholder="Private notes about this client"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </motion.div>

        {client?.status === 'linked' && clientUserId && trainerId && (
          <>
            {planError && (
              <div className={ALERTS.error}>{planError}</div>
            )}
            <TrainingPlanSection
              rotationTemplateIds={plan?.rotationTemplates ?? []}
              lastCompletedTemplateId={plan?.lastCompletedTemplateId}
              dailyRotationTemplateIds={plan?.dailyRotationTemplateIds ?? []}
              lastCompletedDailyTemplateId={plan?.lastCompletedDailyTemplateId}
              allTemplates={templates}
              libraryTemplates={templates.filter((t) => !t.clientId)}
              onAddToRotation={addToRotation}
              onRemoveFromRotation={removeFromRotation}
              onReorderRotation={reorderRotation}
              onAddToDailyRotation={addToDailyRotation}
              onRemoveFromDailyRotation={removeFromDailyRotation}
              onReorderDailyRotation={reorderDailyRotation}
              onEditTemplate={(templateId) => navigate(`/trainer/workouts?templateId=${templateId}`)}
              loading={planLoading}
            />
          </>
        )}

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-2xl p-4 border border-white/10 space-y-3"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Upcoming assignments</h2>
          </div>
          {client?.status === 'linked' ? (
            <AssignmentList assignments={assignments} templates={templates} hideClientName />
          ) : (
            <p className="text-sm text-slate-300">Assignments appear after the client accepts the invite.</p>
          )}
          {client?.status === 'linked' && (
            <p className="text-xs text-slate-400">
              Scheduled assignments are generated from this client's training plan and booking schedule.
            </p>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-2xl p-4 border border-white/10 space-y-3"
        >
          <h2 className="text-xl font-semibold">Sessions</h2>
          {client?.status === 'linked' ? (
            <TrainerSessionsList
              sessions={sessions}
              onSessionClick={setReviewSessionId}
            />
          ) : (
            <p className="text-sm text-slate-300">Session history appears after the client links their account.</p>
          )}
        </motion.div>
      </div>

      {/* Session Review Panel */}
      {reviewSessionId && (
        <SessionReviewPanel
          isOpen={!!reviewSessionId}
          onClose={() => setReviewSessionId(null)}
          sessionId={reviewSessionId}
        />
      )}

    </div>
  );
}
